import moment from "moment";

import type { Moment } from "moment";

const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: "numeric",
  minute: "numeric",
};

const TIME_FORMAT_SECONDS: Intl.DateTimeFormatOptions = {
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
};

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: "numeric",
  day: "numeric",
  year: "2-digit",
};

const DATE_FORMAT_FULL_YEAR: Intl.DateTimeFormatOptions = {
  month: "numeric",
  day: "numeric",
  year: "numeric",
};

export const formatDateTime = (date?: Date | null, includeSeconds = false) => {
  if (date) {
    const formattedTime = includeSeconds
      ? date.toLocaleTimeString(undefined, TIME_FORMAT_SECONDS)
      : date.toLocaleTimeString(undefined, TIME_FORMAT);
    return `${date.toLocaleDateString(undefined, DATE_FORMAT)} ${formattedTime}`;
  }
  return null;
};

export const formatDate = (date?: Date | null, fullYear = false) => {
  if (date) {
    const dateFormat = fullYear ? DATE_FORMAT_FULL_YEAR : DATE_FORMAT;
    return `${date.toLocaleDateString(undefined, dateFormat)}`;
  }
  return null;
};

export const formatTime = (date?: Date | null, includeSeconds = false) => {
  if (date) {
    const timeFormat = includeSeconds ? TIME_FORMAT_SECONDS : TIME_FORMAT;
    return `${date.toLocaleTimeString(undefined, timeFormat)}`;
  }
  return null;
};

export const formatDuration = (sec: number, fmt = "d[d] h[h] m[m] s[s]") =>
  // return formatted time, trimming any zero value time units except for minutes
  moment.duration(sec, "seconds").format(fmt, { trim: "large", stopTrim: "m" });

export const DATE_MOMENT_FORMAT_STR = "MMMM Do, YYYY hh:mm A";

export const MIN_DATE_FILTER = "2014-01-01T00:00:00.000Z";

export const commonDateFilterRanges: () => Record<string, [Moment, Moment]> = () => {
  return {
    Today: [moment().startOf("day"), moment().endOf("day")],
    "Last 7 Days": [moment().startOf("day").subtract(7, "days"), moment().endOf("day")],
    "Last 30 Days": [moment().startOf("day").subtract(30, "days"), moment().endOf("day")],
    "Last 90 Days": [moment().startOf("day").subtract(90, "days"), moment().endOf("day")],
  };
};

export const daysBetweenDates = (firstDate: Date, secondDate: Date) => {
  // get difference and divide to get from milliseconds to days
  const days = Math.abs((secondDate.getTime() - firstDate.getTime()) / (1000 * 3600 * 24));
  return `${days.toFixed(0)} Day${days.toFixed(0) === "1" ? "" : "s"}`;
};

export const secondsToMinutesSeconds = (seconds: number) =>
  (seconds - (seconds %= 60)) / 60 + (9 < seconds ? ":" : ":0") + seconds;

export const getDateRangeForLastMonth = () => ({
  takeoffBefore: moment().endOf("day").toISOString(),
  takeoffSince: moment().subtract(1, "M").startOf("day").toISOString(),
});

// Date-time format for ListItem components
export const formatListDate = (timestamp: string) =>
  moment(timestamp).format("MM/DD/YYYY [at] hh:mm A");

/**
 * A more nuanced spin on commonDateFilterRanges above that takes in a date and
 * returns a string representing the category the date falls in.
 * @param m moment representing the date.
 * @returns string representing category nickname. Ex. "Today", "January 4, 2024"
 */
export const dateToTextualDescription = (m: Moment) => {
  const today = moment();
  if (m.isSame(today, "day")) return "Today";
  else if (m.isSame(today.clone().subtract(1, "days"), "day")) return "Yesterday";
  else if (m.isSameOrAfter(today.clone().subtract(30, "days"), "day"))
    return m.format("MMMM D, YYYY");
  return "Over 30 days ago";
};

/**
 * Sorts a list of items by their descending date, and groups them into categories
 * found in dateToTextualDescription() above.
 * Ex. flights on TransparencyDashboardFlightList.tsx.
 * @param items: list of specified type T
 * @param dateMapper: function to convert T to a date
 * @returns a list of [category, T] groups.
 */
export const groupItemsByDate = <T>(
  items: T[],
  dateMapper: (item: T) => Moment
): [string, T[]][] => {
  const groups: [string, T[]][] = [];
  const sortedItems = [...items].sort((a, b) => dateMapper(b).diff(dateMapper(a)));

  for (const item of sortedItems) {
    const category = dateToTextualDescription(dateMapper(item));
    if (groups?.[groups.length - 1]?.[0] === category) {
      groups[groups.length - 1][1].push(item);
    } else {
      groups.push([category, [item]]);
    }
  }
  return groups;
};

export const isoStringToDateWithMonthNameAndTimezone = (dateString: string | null | undefined) => {
  if (dateString) {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      timeZoneName: "short",
    });
  }
  return null;
};
